import { useEffect, useRef, useState } from 'react'
import { useMutedSeats } from './settings'
import type { ChatMessage } from './types'
import { say, usePhrases } from './usePhrases'

/**
 * Сколько живёт пузырь над карточкой игрока.
 *
 * Шесть секунд — на прочтение короткой фразы и на то, чтобы понять, кто её
 * сказал. Дольше он превращается в подпись к двору, короче — не успевает
 * попасться на глаза тому, кто смотрит на свою половину доски.
 */
const LIFE_MS = 6000

/**
 * В пузыре лежит САМА реплика, а не готовый текст: словарь фраз мог ещё не
 * доехать, и текст, посчитанный один раз при появлении, остался бы кодом
 * до самого гашения. Формулировка поэтому берётся на отрисовке.
 */
type Bubble = { kind: string; body: string; until: number }

/**
 * Что сказано только что — по местам за столом.
 *
 * Нужно пузырям над карточками во дворах: так реплику видно, не открывая
 * шторку, и сразу понятно, кто её сказал.
 *
 * История в пузыри не превращается: при входе в партию и при догоне после
 * обрыва связи приезжает сразу весь хвост разговора, и без отсечки доска
 * покрылась бы пузырями разом. Поэтому первая загрузка только запоминается
 * номером — показывается то, что новее его.
 */
export function useSpeech(
  messages: ChatMessage[] | undefined,
  muteKey: string,
  enabled: boolean,
): Map<number, string> {
  const phrases = usePhrases(enabled)
  const muted = useMutedSeats(muteKey)
  const [bubbles, setBubbles] = useState<Map<number, Bubble>>(new Map())
  const seen = useRef<number | null>(null)

  useEffect(() => {
    // undefined — разговор ещё не загрузился; пустой массив — загрузился и
    // пуст, и отметку тогда надо поставить нулём: иначе первая реплика за
    // только что начатым столом сойдёт за историю и пузыря не даст.
    if (messages === undefined) return

    const last = messages[messages.length - 1]

    if (seen.current === null) {
      seen.current = last?.seq ?? 0

      return
    }

    const before = seen.current

    if (last === undefined || last.seq <= before) return

    seen.current = last.seq

    const fresh = messages.filter((message) => message.seq > before)

    setBubbles((shown) => {
      const next = new Map(shown)

      for (const message of fresh) {
        // Реплика перебивает прежнюю того же игрока: пузырь один на двор,
        // а очередь из них читалась бы дольше, чем идёт партия.
        next.set(message.seat, {
          kind: message.kind,
          body: message.body,
          until: Date.now() + LIFE_MS,
        })
      }

      return next
    })
  }, [messages])

  /*
   * Гашение — одним таймером на ближайший срок, а не по таймеру на пузырь:
   * таймеров иначе накапливается по числу реплик, и каждый держит замыкание
   * на состояние, которого уже нет.
   */
  useEffect(() => {
    if (bubbles.size === 0) return

    const soonest = Math.min(...[...bubbles.values()].map((bubble) => bubble.until))
    const timer = setTimeout(
      () => {
        const now = Date.now()

        setBubbles((shown) => new Map([...shown].filter(([, bubble]) => bubble.until > now)))
      },
      Math.max(0, soonest - Date.now()),
    )

    return () => clearTimeout(timer)
  }, [bubbles])

  const visible = new Map<number, string>()

  for (const [seat, bubble] of bubbles) {
    if (!muted.includes(seat)) visible.set(seat, say(phrases.data, bubble.kind, bubble.body))
  }

  return visible
}
